import { showThemeSafeNotice } from "./ui/themeSafeNotice.js";
import { SPECIAL_EQUIPMENT_SUBTYPES, specialEquipmentSubtypeLabel } from "./vehicle/vehicleCatalog.js";
// REISOR_STAGE22P2Q3_DRIVER_PRIORITY_ORDER_DISCIPLINE_POOL_AND_CLIENT_FEED_V1
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import NativeSafeSelect from "./ui/NativeSafeSelect.jsx";
import DriverDocsCamera from "./DriverDocsCamera.jsx";
import DriverWorkCountriesPanel from "./DriverWorkCountriesPanel.jsx";
import {
  UserRound,
  Truck,
  ChevronDown,
} from "lucide-react";

// === CANON: кузов/загрузка (как у экспедитора) ===
const CANON_BODY_TYPES = [
  "Тент",
  "Фургон",
  "Рефрижератор",
  "Изотерм",
  "Контейнер",
  "Платформа/борт",
  "Mega",
  "Фура / паровоз",
  "Исузу",
  "Бонго",
  "Газель",
  "Лабо",
  "Самосвал",
  "Спецтехника",
  "Другое",
];

const CARRIER_STATUSES = [
  { value: "ip", key: "driverCarrierStatus.ip", fallback: "ИП" },
  { value: "self_employed", key: "driverCarrierStatus.self_employed", fallback: "Самозанятый" },
  { value: "company", key: "driverCarrierStatus.company", fallback: "ООО / компания" },
  { value: "park_logist", key: "driverCarrierStatus.park_logist", fallback: "Парк / логист" },
];

const LEGACY_CARRIER_STATUS_OPTIONS = [
  { value: "physical_park", key: "driverCarrierStatus.physical_park", fallback: "Парк экспедитора (старый)" },
];

const statusNeedsInn = (status) => ["ip", "self_employed", "company"].includes(String(status || ""));
const statusNeedsName = (status) => ["company", "park_logist"].includes(String(status || ""));


const S = (v) => (v === undefined || v === null ? "" : String(v));
const trim = (v) => S(v).trim();

function splitFeatures(v) {
  if (Array.isArray(v)) return v.map((x) => trim(x)).filter(Boolean);
  const s = trim(v);
  if (!s) return [];
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}
function joinFeatures(v) {
  return splitFeatures(v).join(", ");
}


// REISOR_STAGE22J_CLIENT_DRIVER_PRIORITY_RATING_OFFERS_V1
// REISOR_STAGE22K_PRIORITY_LANGUAGE_AND_COMPAT_CLEANUP_V1
// REISOR_STAGE22N_B_DRIVER_PRIORITY_UI_COPY_V1
// REISOR_STAGE22N_C_DRIVER_PRIORITY_PRODUCT_UI_V1
// REISOR_STAGE22N_E_PRIORITY_HISTORY_V1
// REISOR_STAGE22P2C1_DRIVER_ACCEPT_SOFT_PENALTY_V1
const DP22N_PRIORITY_LABEL_KEYS = {
  completed_order: "priority.items.completedOrder",
  completed_orders: "priority.items.completedOrders",
  cashless_completed: "priority.items.cashlessCompleted",
  vat_completed: "priority.items.vatCompleted",
  ip_status: "priority.items.ipStatus",
  rating_4_90: "priority.items.rating490",
  rating_4_95: "priority.items.rating495",
  car_age_7: "priority.items.carAge7",
  car_age_5: "priority.items.carAge5",
  order_failure: "priority.items.orderFailure",
  driver_accept_responsibility: "priority.items.driverAcceptResponsibility",
  confirmed_claim: "priority.items.confirmedClaim",
};

const DP22N_PRIORITY_HISTORY_REASON_KEYS = {
  driver_accept_timeout: "priority.history.reasons.acceptTimeout",
  driver_accept_miss: "priority.history.reasons.acceptTimeout",
  driver_declined_before_accept: "priority.history.reasons.driverDeclined",
  driver_declined_after_assignment: "priority.history.reasons.driverDeclined",
  driver_declined_after_selection: "priority.history.reasons.driverDeclined",
  driver_declined: "priority.history.reasons.driverDeclined",
  driver_rejected_before_accept: "priority.history.reasons.driverDeclined",
  driver_reject_before_accept: "priority.history.reasons.driverDeclined",
  driver_cancel_before_accept: "priority.history.reasons.driverDeclined",
  dispatcher_restore_priority: "priority.history.reasons.dispatcherRestore",
  dispatcher_priority_restore: "priority.history.reasons.dispatcherRestore",
  priority_restore: "priority.history.reasons.dispatcherRestore",
  manual_priority_restore: "priority.history.reasons.dispatcherRestore",
  restore_priority: "priority.history.reasons.dispatcherRestore",
  driver_priority_restore: "priority.history.reasons.dispatcherRestore",
};

const DP22N_PRIORITY_HINT_KEYS = {
  completed_orders: "priority.hints.completedOrders",
  cashless_completed: "priority.hints.cashlessCompleted",
  vat_completed: "priority.hints.vatCompleted",
  rating_4_90: "priority.hints.rating",
  rating_4_95: "priority.hints.rating",
};

const DP22N_PRIORITY_FORMULA = [
  { code: "completed_orders", labelKey: "priority.formula.completedOrders", pointsKey: "priority.formula.completedOrdersPoints", label: "Выполненные заказы", points: "+5 за заказ, максимум +30" },
  { code: "cashless_completed", labelKey: "priority.formula.cashlessCompleted", pointsKey: "priority.formula.plus20", label: "Выполненный заказ с безналичной оплатой", points: "+20" },
  { code: "vat_completed", labelKey: "priority.formula.vatCompleted", pointsKey: "priority.formula.plus20", label: "Выполненный заказ с НДС", points: "+20" },
  { code: "ip_status", labelKey: "priority.formula.ipStatus", pointsKey: "priority.formula.plus30", label: "Статус ИП", points: "+30" },
  { code: "rating_4_90", labelKey: "priority.formula.rating490", pointsKey: "priority.formula.plus10", label: "Рейтинг 4.90–4.94 после включения рейтинга", points: "+10" },
  { code: "rating_4_95", labelKey: "priority.formula.rating495", pointsKey: "priority.formula.plus20", label: "Рейтинг 4.95+ после включения рейтинга", points: "+20" },
  { code: "car_age_7", labelKey: "priority.formula.carAge7", pointsKey: "priority.formula.plus10", label: "Авто до 7 лет", points: "+10" },
  { code: "car_age_5", labelKey: "priority.formula.carAge5", pointsKey: "priority.formula.plus20", label: "Авто до 5 лет", points: "+20" },
];

function dp22nTr(tr, key, fallback) {
  return typeof tr === "function" ? tr(key, fallback) : fallback;
}

function dp22nPriorityItemLabel(tr, item) {
  const code = trim(item?.code);
  const key = DP22N_PRIORITY_LABEL_KEYS[code];
  return key ? dp22nTr(tr, key, item?.label || code) : (item?.label || code || "");
}

function dp22nPriorityItemHint(tr, item) {
  const code = trim(item?.code);
  const key = DP22N_PRIORITY_HINT_KEYS[code];
  if (key) return dp22nTr(tr, key, item?.hint || "");
  return item?.hint || "";
}

function dp22jPriorityScore(trust) {
  const n = Math.round(Number(
    trust?.priorityScore ??
    trust?.priority ??
    trust?.score ??
    trust?.trustPercent ??
    trust?.trustScore ??
    0
  ));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function dp22jPriorityTone(scoreRaw) {
  const score = dp22jPriorityScore({ priorityScore: scoreRaw });
  if (score <= 40) return { text: "#fecaca", bg: "rgba(239,68,68,0.16)", border: "rgba(239,68,68,0.50)", glow: "rgba(239,68,68,0.18)" };
  if (score <= 90) return { text: "#fde68a", bg: "rgba(245,158,11,0.16)", border: "rgba(245,158,11,0.50)", glow: "rgba(245,158,11,0.18)" };
  return { text: "#bbf7d0", bg: "rgba(34,197,94,0.16)", border: "rgba(34,197,94,0.46)", glow: "rgba(34,197,94,0.18)" };
}

function Dp22jPriorityCircle({ trust, title = "Приоритет REISOR" }) {
  const score = dp22jPriorityScore(trust);
  const tone = dp22jPriorityTone(score);
  return (
    <div
      className="shrink-0"
      style={{
        width: 92,
        height: 92,
        borderRadius: "50%",
        border: `2px solid ${tone.border}`,
        background: `radial-gradient(circle at 35% 25%, rgba(255,255,255,0.18), transparent 28%), ${tone.bg}`,
        color: tone.text,
        boxShadow: `0 0 0 8px ${tone.glow}, inset 0 1px 0 rgba(255,255,255,0.12)`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 950,
        fontSize: 32,
        lineHeight: 1,
      }}
      title={title}
    >
      {score}
    </div>
  );
}


// REISOR_STAGE22N_C_DRIVER_PRIORITY_PRODUCT_UI_V1
const DP22N_C_PRIORITY_MAX = 140;

const DP22N_C_GOAL_RULES = [
  { code: "completed_orders", group: "completed", maxPoints: 30, labelKey: "priority.formula.completedOrders", fallback: "Выполненные заказы", hintKey: "priority.hints.completedOrders" },
  { code: "ip_status", group: "single", maxPoints: 30, labelKey: "priority.formula.ipStatus", fallback: "Статус ИП" },
  { code: "cashless_completed", group: "single", maxPoints: 20, labelKey: "priority.formula.cashlessCompleted", fallback: "Выполненный заказ с безналичной оплатой", hintKey: "priority.hints.cashlessCompleted" },
  { code: "vat_completed", group: "single", maxPoints: 20, labelKey: "priority.formula.vatCompleted", fallback: "Выполненный заказ с НДС", hintKey: "priority.hints.vatCompleted" },
  { code: "car_age_5", group: "car_age", maxPoints: 20, labelKey: "priority.formula.carAge5", fallback: "Авто до 5 лет" },
  { code: "rating_4_95", group: "rating", maxPoints: 20, labelKey: "priority.formula.rating495", fallback: "Рейтинг 4.95+", hintKey: "priority.hints.rating" },
];

function dp22nSignedPoints(v) {
  const n = Math.round(Number(v || 0));
  if (!Number.isFinite(n) || n === 0) return "0";
  return n > 0 ? `+${n}` : `${n}`;
}

function dp22nEarnedPointsByCode(items) {
  const map = {};
  for (const item of Array.isArray(items) ? items : []) {
    const code = trim(item?.code);
    if (!code) continue;
    const points = Number(item?.points || 0);
    if (code === "completed_orders") {
      // REISOR_STAGE22P2Q11_COMPLETED_ORDERS_POOL_MINUS30_PLUS30_V1
      // completed_orders may be negative; do not coerce it back to 0.
      map[code] = points;
    } else {
      const prev = Object.prototype.hasOwnProperty.call(map, code) ? Number(map[code] || 0) : 0;
      map[code] = Math.max(prev, points);
    }
  }
  return map;
}

function dp22nCurrentForRule(rule, earnedMap) {
  if (rule.group === "car_age") {
    return Math.max(Number(earnedMap.car_age_5 || 0), Number(earnedMap.car_age_7 || 0));
  }
  if (rule.group === "rating") {
    return Math.max(Number(earnedMap.rating_4_95 || 0), Number(earnedMap.rating_4_90 || 0));
  }
  return Number(earnedMap[rule.code] || 0);
}

function dp22nBuildGoalRows(tr, earnedItems) {
  const earnedMap = dp22nEarnedPointsByCode(earnedItems);
  return DP22N_C_GOAL_RULES
    .map((rule) => {
      const rawCurrent = dp22nCurrentForRule(rule, earnedMap);
      const current = rule.code === "completed_orders"
        ? Math.max(-30, Math.min(Number(rule.maxPoints || 0), Number(rawCurrent || 0)))
        : Math.max(0, Number(rawCurrent || 0));
      const max = Number(rule.maxPoints || 0);
      const remaining = Math.max(0, max - current);
      if (remaining <= 0) return null;
      return {
        code: rule.code,
        title: dp22nTr(tr, rule.labelKey, rule.fallback),
        hint: rule.hintKey ? dp22nTr(tr, rule.hintKey, "") : "",
        points: remaining,
        current,
        max,
        isCompletedPool: rule.code === "completed_orders",
      };
    })
    .filter(Boolean);
}

function dp22nPriorityStatusText(tr, score) {
  if (score >= 100) return dp22nTr(tr, "priority.statusGreat", "Сильный приоритет");
  if (score >= 70) return dp22nTr(tr, "priority.statusAlmost", "Вы близко к сильному приоритету");
  if (score > 0) return dp22nTr(tr, "priority.statusGrow", "Приоритет уже работает");
  return dp22nTr(tr, "priority.statusStart", "Начните набирать приоритет");
}


function dp22nPriorityRowsForHistory(earnedItems, penaltyItems) {
  const rows = [];
  for (const item of Array.isArray(earnedItems) ? earnedItems : []) {
    if (Number(item?.points || 0) > 0) rows.push({ ...item, direction: "up" });
  }
  for (const item of Array.isArray(penaltyItems) ? penaltyItems : []) {
    if (Number(item?.points || 0) < 0) rows.push({ ...item, direction: "down" });
  }
  return rows.sort((a, b) => Math.abs(Number(b.points || 0)) - Math.abs(Number(a.points || 0)));
}

function Dp22nPriorityGauge({ score, tr }) {
  const pct = Math.max(0, Math.min(100, Math.round((Number(score || 0) / DP22N_C_PRIORITY_MAX) * 100)));
  return (
    <div className="driver-priority-gauge mx-auto mt-4 flex h-44 w-44 items-center justify-center rounded-full p-[9px]"
      style={{
        background: `conic-gradient(#22c55e ${pct}%, rgba(148,163,184,0.22) ${pct}% 100%)`,
        boxShadow: "0 24px 80px rgba(34,197,94,0.16)",
      }}
    >
      <div className="driver-priority-gauge-inner flex h-full w-full flex-col items-center justify-center rounded-full border border-white/10 bg-slate-950/95">
        <div className="driver-priority-gauge-score text-6xl font-black leading-none tracking-tight text-white">{score}</div>
        <div className="mt-2 text-center text-xs font-semibold text-slate-300">{dp22nTr(tr, "priority.currentPriority", "Текущий приоритет")}</div>
      </div>
    </div>
  );
}

function Dp22nPriorityRow({ title, hint, points, tone = "green", rightText = "" }) {
  const isRed = tone === "red";
  const icon = isRed ? "−" : "✓";
  return (
    <div className="driver-priority-row flex items-center gap-3 border-b border-white/10 py-4 last:border-b-0">
      <div className={`driver-priority-row-icon flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-lg font-black ${isRed ? "bg-red-400/15 text-red-200" : "bg-emerald-400/15 text-emerald-200"}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="driver-priority-row-title text-base font-extrabold leading-snug text-white">{title}</div>
        {hint ? <div className="mt-1 text-xs leading-snug text-slate-400">{hint}</div> : null}
      </div>
      <div className={`driver-priority-row-points shrink-0 text-right text-2xl font-black ${isRed ? "text-red-200" : "text-emerald-200"}`}>
        {rightText || dp22nSignedPoints(points)}
      </div>
    </div>
  );
}

function dp22nHistoryIso(item) {
  return item?.eventAt || item?.createdAt || item?.updatedAt || "";
}

function dp22nHistoryIntlLocale(lang) {
  const code = trim(lang).toLowerCase();
  if (code.startsWith("uz")) return "uz-Cyrl-UZ";
  if (code.startsWith("tr")) return "tr-TR";
  return "ru-RU";
}

function dp22nHistoryTimeLabel(iso, lang) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleTimeString(dp22nHistoryIntlLocale(lang), { hour: "2-digit", minute: "2-digit" });
}

function dp22nHistoryDayLabel(tr, iso, lang) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return dp22nTr(tr, "priority.history.currentState", "Текущее состояние");
  const start = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const today = start(new Date());
  const day = start(d);
  if (day === today) return dp22nTr(tr, "priority.history.today", "Сегодня");
  if (day === today - 86400000) return dp22nTr(tr, "priority.history.yesterday", "Вчера");
  return d.toLocaleDateString(dp22nHistoryIntlLocale(lang), { day: "numeric", month: "long" });
}

function dp22nHistoryReasonLabel(tr, item) {
  const code = trim(item?.reasonCode || item?.code);
  const historyKey = DP22N_PRIORITY_HISTORY_REASON_KEYS[code];
  if (historyKey) return dp22nTr(tr, historyKey, item?.reasonLabel || code);
  if (item?.reasonLabel) {
    const mapped = dp22nPriorityItemLabel(tr, { ...item, code, label: item.reasonLabel });
    return mapped || item.reasonLabel;
  }
  return dp22nPriorityItemLabel(tr, item);
}

function dp22nGroupHistoryRows(rows, tr, lang) {
  const groups = [];
  const map = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const key = dp22nHistoryDayLabel(tr, dp22nHistoryIso(row), lang);
    if (!map.has(key)) {
      const group = { key, rows: [] };
      map.set(key, group);
      groups.push(group);
    }
    map.get(key).rows.push(row);
  }
  return groups;
}

function Dp22nPriorityHistoryRow({ tr, item, lang }) {
  const points = Number(item?.delta ?? item?.points ?? 0);
  const positive = points >= 0;
  const title = positive
    ? dp22nTr(tr, "priority.history.gained", "Приоритет получен")
    : dp22nTr(tr, "priority.history.lost", "Приоритет потерян");
  const time = dp22nHistoryTimeLabel(dp22nHistoryIso(item), lang);
  const reason = dp22nHistoryReasonLabel(tr, item);

  return (
    <div className="driver-priority-history-row rounded-[26px] border border-white/10 bg-white/[0.045] px-4 py-4 shadow-lg shadow-black/10">
      <div className="flex items-start gap-3">
        <div className={`driver-priority-history-icon mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg font-black ${positive ? "bg-emerald-400/15 text-emerald-200" : "bg-red-400/15 text-red-200"}`}>
          {positive ? "+" : "−"}
        </div>
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-extrabold ${positive ? "text-emerald-200" : "text-red-200"}`}>
            {time ? `${time} · ` : ""}{title}
          </div>
          <div className="driver-priority-history-reason mt-1 text-xl font-black leading-tight text-white">
            {reason}
          </div>
          {item?.meta?.orderNo ? (
            <div className="mt-1 text-xs text-slate-400">
              {dp22nTr(tr, "priority.history.order", "Заказ")} №{item.meta.orderNo}
            </div>
          ) : null}
        </div>
        <div className={`driver-priority-history-points shrink-0 text-3xl font-black ${positive ? "text-emerald-200" : "text-red-200"}`}>
          {dp22nSignedPoints(points)}
        </div>
      </div>
    </div>
  );
}

function Dp22nPriorityHistoryScreen({ tr, score, rows, onBack, realHistory = false, lang = "ru" }) {
  const groups = dp22nGroupHistoryRows(rows, tr, lang);
  return (
    <div className="driver-priority-history-screen profile-safe-bottom mx-auto w-full max-w-3xl p-4 text-slate-100">
      {/* REISOR_STAGE22N_E_PRIORITY_HISTORY_V1 */}
      <div className="driver-priority-history-header flex items-center gap-3 py-2">
        <button type="button" onClick={onBack} className="flex h-11 w-11 items-center justify-center rounded-full bg-white/[0.08] text-2xl font-black text-white active:scale-95">
          ←
        </button>
        <div className="text-xl font-black">{dp22nTr(tr, "priority.history.title", "История изменений приоритета")}</div>
      </div>

      <div className="driver-priority-history-score-card mt-6 rounded-[30px] border border-white/10 bg-white/[0.04] p-6 text-center">
        <div className="driver-priority-history-score text-6xl font-black leading-none text-white">{score}</div>
        <div className="mt-2 text-sm font-semibold text-slate-300">{dp22nTr(tr, "priority.currentPriority", "Текущий приоритет")}</div>
        <div className="mt-3 text-xs leading-relaxed text-slate-400">
          {dp22nTr(tr, "priority.history.note", "Здесь сохраняются подтверждённые изменения приоритета: заказы, бонусы и штрафы.")}
        </div>
      </div>

      <div className="mt-5">
        {groups.length ? (
          <div className="grid gap-5">
            {groups.map((group) => (
              <section key={group.key}>
                <div className="mb-2 px-1 text-sm font-black uppercase tracking-[0.12em] text-slate-400">{group.key}</div>
                <div className="driver-priority-history-group grid gap-3">
                  {group.rows.map((item, idx) => (
                    <Dp22nPriorityHistoryRow key={`${item.id || item.eventKey || item.reasonCode || item.code || "row"}-${idx}`} tr={tr} item={item} lang={lang} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="rounded-[30px] border border-white/10 bg-white/[0.04] p-5">
            <div className="text-lg font-black">{dp22nTr(tr, "priority.history.emptyTitle", "История пока пустая")}</div>
            <div className="mt-2 text-sm leading-relaxed text-slate-400">
              {dp22nTr(tr, "priority.history.emptyText", "Она появится после завершённых заказов, подтверждённых бонусов или штрафов.")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function mapStatus(s) {
  const v = trim(s).toLowerCase();
  if (v === "approved") return "approved";
  if (v === "blocked") return "blocked";
  if (v === "pending") return "pending";
  return v || "draft";
}

function getStoredToken() {
  if (typeof window === "undefined") return "";
  const storages = [window.localStorage, window.sessionStorage];
  const keys = ["driverToken","drvToken","driver_token","token","authToken","auth_token","auth","driverAuth","driver_data","driver"];
  for (const st of storages) {
    for (const k of keys) {
      const raw = st.getItem(k);
      if (!raw) continue;
      try {
        const obj = JSON.parse(raw);
        if (typeof obj === "string") return obj;
        if (obj?.token) return obj.token;
        if (obj?.accessToken) return obj.accessToken;
        if (obj?.authToken) return obj.authToken;
        if (obj?.driverToken) return obj.driverToken;
        if (obj?.jwt) return obj.jwt;
      } catch {
        return raw;
      }
    }
  }
  return "";
}

function getStoredDriverId() {
  if (typeof window === "undefined") return "";
  const direct = trim(window.localStorage.getItem("driver_id"));
  if (direct) return direct;

  // fallback: иногда driver_id не сохранился, но есть driver_data
  try {
    const raw = window.localStorage.getItem("driver_data");
    const d = raw ? JSON.parse(raw) : {};
    return trim(d?._id || d?.id || d?.driverId || "");
  } catch {
    return "";
  }
}

function resetDriverAuthAndReturnToLogin() {
  if (typeof window === "undefined") return;

  const storages = [window.localStorage, window.sessionStorage];
  const keys = [
    "driverToken",
    "drvToken",
    "driver_token",
    "token",
    "authToken",
    "auth_token",
    "driver_id",
    "driver_data",
    "driver",
    "driverAuth",
    "auth",
  ];

  for (const st of storages) {
    for (const k of keys) {
      try {
        st.removeItem(k);
      } catch {}
    }
  }

  try {
    window.dispatchEvent(new Event("gptcargo_driver_auth_changed"));
  } catch {}
  try {
    window.dispatchEvent(new Event("gptcargo_driver_force_login"));
  } catch {}
}

function canonBodyType(rawBody, rawOther) {
  const s = trim(rawBody);
  const other = trim(rawOther);
  if (!s) return { bodyType: "", bodyTypeOther: "" };

  const found = CANON_BODY_TYPES.find((x) => x.toLowerCase() === s.toLowerCase());
  if (
    found &&
    !["Другое", "Спецтехника"].includes(found)
  ) {
    return {
      bodyType: found,
      bodyTypeOther: "",
    };
  }

  if (found === "Спецтехника") {
    return {
      bodyType: "Спецтехника",
      bodyTypeOther: other,
    };
  }
  if (found === "Другое") return { bodyType: "Другое", bodyTypeOther: other };

  // unknown => treat as "Другое"
  return { bodyType: "Другое", bodyTypeOther: other || s };
}

export default function DriverProfile() {
  const { t, i18n } = useTranslation();
  const activeLang = String(i18n.resolvedLanguage || i18n.language || "ru").split("-")[0];
  const tr = (key, fallback) => {
    try {
      return t(key, { defaultValue: fallback });
    } catch {
      return fallback;
    }
  };
  const bodyTypeLabel = (value) => tr(`driverOptions.bodyTypes.${value}`, value);

  const [token, setToken] = useState(() => getStoredToken());
  const [driverId, setDriverId] = useState(() => getStoredDriverId());

  // keep auth reactive (registration/login updates storage after first mount)
  useEffect(() => {
    const sync = () => {
      try {
        setToken(getStoredToken());
        setDriverId(getStoredDriverId());
      } catch {}
    };
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener("gptcargo_driver_auth_changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("gptcargo_driver_auth_changed", sync);
    };
  }, []);
const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workStatusSaving, setWorkStatusSaving] = useState(false);
  const [error, setError] = useState("");

  const [reputation, setReputation] = useState(null);
  const [reputationLoading, setReputationLoading] = useState(false);
  // REISOR_STAGE22I_DRIVER_TRUST_SCORE_RULES_AND_UI_V1
  const [driverTrust, setDriverTrust] = useState(null);
  // REISOR_STAGE22N_C_DRIVER_PRIORITY_PRODUCT_UI_V1
  const [priorityScreen, setPriorityScreen] = useState("profile");
  const [priorityHistory, setPriorityHistory] = useState([]); // REISOR_STAGE22N_E_PRIORITY_HISTORY_V1

  const [status, setStatus] = useState("draft");
  const [pendingEditMode, setPendingEditMode] = useState(false);
  const [draftEditMode, setDraftEditMode] = useState(false);
  // CANON (23 Feb 2026): draft/pending — можно править; approved/blocked — правки запрещены
  const st = mapStatus(status);
  const locked = ["blocked", "approved"].includes(st);
  const isApproved = st === "approved";
  const isPending = st === "pending";
  const isBlocked = st === "blocked";

  const [bodyTypeOther, setBodyTypeOther] = useState("");

  // anti-wipe: после первого hydrate не стираем при редких перезагрузках/языке
  const hydratedOnceRef = useRef(false);
  const retry429Ref = useRef({ inFlight: false, ts: 0 });

  const [parkItems, setParkItems] = useState([]);
  const [parkLoading, setParkLoading] = useState(false);
  const [parkSaving, setParkSaving] = useState(false);

  const [form, setForm] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    phoneMain: "",
    phoneExtra: "",
    carrierStatus: "",
    inn: "",
    carrierName: "",
    selectedParkExpeditorId: "",
    car: "",
    carYear: "",
    carNumber: "",
    bodyType: "",
    features: "",
    licenseNumber: "",
    cardNumber: "",
    bank: "",
    paymentCashless: false,
    paymentVat: false,
  });

  const authHeaders = useMemo(() => {
    const h = {};
    if (token) h.Authorization = /^bearer\s+/i.test(token) ? token : `Bearer ${token}`;
    return h;
  }, [token]);

  async function apiFetch(url, opts = {}) {
    const headers = { ...(opts.headers || {}), ...authHeaders };
    const res = await fetch(url, { ...opts, headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.error || data?.message || `HTTP_${res.status}`;
      throw new Error(msg);
    }
    return data;
  }

  async function loadReputation() {
    if (!driverId) {
      setReputation(null);
      return;
    }
    setReputationLoading(true);
    try {
      const res = await fetch(`/api/drivers/reputation/${encodeURIComponent(driverId)}`);
      const data = await res.json().catch(() => ({}));
      if (res.ok && data?.ok) {
        setReputation(data?.summary || null);
      } else {
        setReputation(null);
      }
    } catch {
      setReputation(null);
    } finally {
      setReputationLoading(false);
    }
  }

  async function loadParkList() {
    if (!token) return;
    setParkLoading(true);
    try {
      const data = await apiFetch("/api/driver/park/list");
      setParkItems(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setParkItems([]);
    } finally {
      setParkLoading(false);
    }
  }

  async function selectPark(expeditorId) {
    const id = trim(expeditorId);
    if (!id) return;
    setParkSaving(true);
    try {
      const data = await apiFetch("/api/driver/park/select", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ expeditorId: id }),
      });
      setForm((p) => ({ ...p, selectedParkExpeditorId: data?.parkId || id }));
      showThemeSafeNotice(tr("profile.alerts.parkSelected", "Парк выбран"));
    } catch (e) {
      showThemeSafeNotice(`${tr("profile.alerts.parkSelectError", "Не удалось выбрать парк")}: ${String(e?.message || e)}`);
    } finally {
      setParkSaving(false);
    }
  }

  async function loadProfile() {
    if (!token) {
      resetDriverAuthAndReturnToLogin();
      setLoading(false);
      setError("NO_AUTH");
      return;
    }
    if (!driverId) {
      resetDriverAuthAndReturnToLogin();
      setLoading(false);
      setError("DRIVER_ID_MISSING");
      return;
    }
    setLoading(true);
    setError("");

    try {
      const data = await apiFetch(`/api/drivers/${encodeURIComponent(driverId)}/profile`);
      const d = data?.driver || data || {};
      setDriverTrust(d.driverTrust || d.trust || data?.driverTrust || data?.trust || null);
      setPriorityHistory(Array.isArray(d.priorityHistory) ? d.priorityHistory : (Array.isArray(data?.priorityHistory) ? data.priorityHistory : [])); // REISOR_STAGE22N_E_PRIORITY_HISTORY_V1
      setStatus(mapStatus(d.status));
      // sync status into driver_data so tabs unlock immediately after approval
      try {
        const stNow = mapStatus(d.status);
        const raw = localStorage.getItem("driver_data");
        const prev = raw ? JSON.parse(raw) : {};
        const snap = { ...prev, status: stNow, _id: driverId, id: driverId, driverId };
        localStorage.setItem("driver_data", JSON.stringify(snap));
        window.dispatchEvent(new Event("gptcargo_driver_auth_changed"));
      } catch {}

      if (!hydratedOnceRef.current) {
        hydratedOnceRef.current = true;

        const bt = canonBodyType(d.bodyType, d.bodyTypeOther);
        setBodyTypeOther(bt.bodyTypeOther || "");

        setForm({
          lastName: d.lastName || "",
          firstName: d.firstName || "",
          middleName: d.middleName || "",
          phoneMain: d.phoneMain || "",
          phoneExtra: d.phoneExtra || "",
          carrierStatus: d.carrierStatus || "",
          inn: d.inn || "",
          carrierName: d.carrierName || "",
          selectedParkExpeditorId: d.selectedParkExpeditorId || "",
          car: d.car || "",
          carYear: d.carYear || d.vehicleYear || d.car_year || "",
          carNumber: d.carNumber || "",
          bodyType: bt.bodyType || "",
          features: joinFeatures(d.features || d.featuresText || ""),
          licenseNumber: d.licenseNumber || "",
          cardNumber: d.cardNumber || "",
          bank: d.bank || "",
          paymentCashless: !!(d.paymentCapabilities?.cashless || d.worksCashless || d.cashlessEnabled),
          paymentVat: !!(d.paymentCapabilities?.vat || d.worksWithVat || d.vatEnabled),
        });
      }
    } catch (e) {
      const msg = String(e?.message || e);

      if (msg === "NO_AUTH" || msg === "HTTP_401" || msg === "HTTP_403") {
        resetDriverAuthAndReturnToLogin();
        setError("NO_AUTH");
        return;
      }

      // nginx limit_req/limit_conn may return 429 under bursts
      if (msg === "HTTP_429" || /429/.test(msg)) {
        setError("HTTP_429");
        const now = Date.now();
        // cool-down: do not spam retries
        if (!retry429Ref.current.inFlight && now - retry429Ref.current.ts > 2500) {
          retry429Ref.current.inFlight = true;
          retry429Ref.current.ts = now;
          setTimeout(() => {
            retry429Ref.current.inFlight = false;
            // retry only if still mounted and still same auth
            try { loadProfile(); } catch {}
          }, 2500);
        }
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadProfile();
    loadReputation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId, token]);

  useEffect(() => {
    if (form.carrierStatus === "physical_park") {
      loadParkList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.carrierStatus, token]);

  // PROFILE_AUTO_REFRESH_V1: while pending, refresh status every 15s to unlock tabs after dispatcher approval
  useEffect(() => {
    if (!token || !driverId) return;
    if (!isPending) return;
    const t = setInterval(() => {
      try { loadProfile(); } catch {}
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, driverId, isPending]);

  async function saveProfile() {
    if (!driverId) return;

    // Registration draft/pending no longer requires legal work-status fields.
    if (!["draft", "pending"].includes(st)) {
      if (!trim(form.carrierStatus)) return showThemeSafeNotice(tr("profile.alerts.carrierStatusRequired", "Выберите статус работы"));
      if (statusNeedsInn(form.carrierStatus) && !trim(form.inn)) return showThemeSafeNotice(tr("profile.alerts.innRequired", "Укажите ИНН"));
      if (statusNeedsName(form.carrierStatus) && !trim(form.carrierName)) return showThemeSafeNotice(tr("profile.alerts.carrierNameRequired", "Укажите название компании, парка или логиста"));
    }
    if (!form.bodyType) return showThemeSafeNotice(tr("profile.alerts.pickBody", "Select body type"));
    if (["Другое", "Спецтехника"].includes(form.bodyType) && !trim(bodyTypeOther)) return showThemeSafeNotice(tr("profile.alerts.clarifyBody", "Specify body type"));

    setSaving(true);
    setError("");
    try {
      const payload = {
        lastName: form.lastName,
        firstName: form.firstName,
        middleName: form.middleName,
        phoneMain: form.phoneMain,
        phoneExtra: form.phoneExtra,
        carrierStatus: form.carrierStatus,
        inn: form.inn,
        carrierName: form.carrierName,
        selectedParkExpeditorId: form.selectedParkExpeditorId,
        car: form.car,
        carYear: form.carYear,
        carNumber: form.carNumber,
        bodyType: form.bodyType,
        bodyTypeOther: ["Другое", "Спецтехника"].includes(form.bodyType) ? trim(bodyTypeOther) : "",
        features: splitFeatures(form.features),
        licenseNumber: form.licenseNumber,
        cardNumber: form.cardNumber,
        bank: form.bank,
        paymentCapabilities: {
          cashless: !!form.paymentCashless,
          vat: !!form.paymentVat,
        },
      };

      const data = await apiFetch(`/api/drivers/${encodeURIComponent(driverId)}/profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      setStatus(mapStatus(data?.status || status));
      hydratedOnceRef.current = true;
      showThemeSafeNotice(tr("profile.alerts.saved", "Saved"));
      return true;
    } catch (e) {
      setError(String(e?.message || e));
      showThemeSafeNotice(`${tr("profile.alerts.saveError", "Save error")}: ${String(e?.message || e)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }

  // REISOR_STAGE97D3_DRIVER_WORK_STATUS_TOP_CARD_V1
  async function saveWorkStatus() {
    if (!driverId) return;

    if (!trim(form.carrierStatus)) return showThemeSafeNotice(tr("profile.alerts.carrierStatusRequired", "Выберите статус работы"));
    if (statusNeedsInn(form.carrierStatus) && !trim(form.inn)) return showThemeSafeNotice(tr("profile.alerts.innRequired", "Укажите ИНН"));
    if (statusNeedsName(form.carrierStatus) && !trim(form.carrierName)) return showThemeSafeNotice(tr("profile.alerts.carrierNameRequired", "Укажите название компании, парка или логиста"));

    setWorkStatusSaving(true);
    setError("");
    try {
      const data = await apiFetch(`/api/drivers/${encodeURIComponent(driverId)}/work-status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          carrierStatus: form.carrierStatus,
          inn: form.inn,
          carrierName: form.carrierName,
        }),
      });

      setForm((p) => ({
        ...p,
        carrierStatus: data?.carrierStatus || p.carrierStatus,
        inn: data?.inn ?? p.inn,
        carrierName: data?.carrierName ?? p.carrierName,
      }));
      showThemeSafeNotice(tr("profile.alerts.workStatusSaved", "Статус работы сохранён"));
    } catch (e) {
      setError(String(e?.message || e));
      showThemeSafeNotice(`${tr("profile.alerts.workStatusSaveError", "Не удалось сохранить статус работы")}: ${String(e?.message || e)}`);
    } finally {
      setWorkStatusSaving(false);
    }
  }

  const disabled = locked || loading || !driverId;
  const workStatusDisabled = isBlocked || loading || !driverId;

  const inputClass =
    "rounded-xl bg-black/30 px-3 py-2 text-sm outline-none border border-white/10 text-white placeholder:text-slate-300/60";

  const approvalPayload = {
    lastName: form.lastName,
    firstName: form.firstName,
    middleName: form.middleName,
    phoneExtra: form.phoneExtra,
    bodyType: form.bodyType,
    bodyTypeOther: ["Другое", "Спецтехника"].includes(form.bodyType) ? trim(bodyTypeOther) : "",
  };

  const approvalFields = (
    <div className="grid gap-4">
      <section className="rounded-3xl border border-white/10 bg-white/5 p-4">
        <div className="mb-3 text-lg font-black">{tr("registrationReview.driverTitle", "Водитель")}</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input className={inputClass} value={form.lastName} onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} placeholder={tr("profile.lastName", "Фамилия")} />
          <input className={inputClass} value={form.firstName} onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} placeholder={tr("profile.firstName", "Имя")} />
          <input className={inputClass} value={form.middleName} onChange={(e) => setForm((p) => ({ ...p, middleName: e.target.value }))} placeholder={tr("profile.patronymic", "Отчество — необязательно")} />
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-4">
        <div className="mb-3 text-lg font-black">{tr("registrationReview.contactsTitle", "Контакты")}</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-bold text-sky-200">{tr("registrationReview.telegramPhone", "Telegram-номер")}</div>
            <input disabled className={`${inputClass} w-full opacity-80`} value={form.phoneMain} />
          </div>
          <div>
            <div className="mb-1 text-xs font-bold text-slate-300">{tr("registrationReview.extraPhone", "Резервный номер")}</div>
            <input className={`${inputClass} w-full`} value={form.phoneExtra} onChange={(e) => setForm((p) => ({ ...p, phoneExtra: e.target.value }))} placeholder={tr("registrationReview.extraPhoneOptional", "Необязательно")} />
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-white/10 bg-white/5 p-4">
        <div className="mb-3 text-lg font-black">{tr("profile.bodyType", "Тип кузова")}</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <NativeSafeSelect
            className="w-full"
            buttonClassName={`${inputClass} w-full text-left`}
            menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
            value={form.bodyType || ""}
            onChange={(value) => {
              setForm((p) => ({ ...p, bodyType: value }));
              if (value !== "Другое") setBodyTypeOther("");
            }}
            ariaLabel={tr("profile.bodyType", "Тип кузова")}
            options={[
              { value: "", label: `${tr("profile.bodyType", "Тип кузова")}…` },
              ...CANON_BODY_TYPES.map((x) => ({ value: x, label: bodyTypeLabel(x) })),
            ]}
          />
          {["Другое", "Спецтехника"].includes(form.bodyType) ? (
          <>
            <input className={inputClass} value={bodyTypeOther} onChange={(e) => setBodyTypeOther(e.target.value)} placeholder={tr("profile.bodyTypeOther", "Уточните кузов")} />

            {form.bodyType === "Спецтехника" ? (
                <div
                  className="reisor-special-subtype-grid"
                  data-reisor-special-subtype-picker="profile"
                >
                  {SPECIAL_EQUIPMENT_SUBTYPES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`reisor-special-subtype-option ${
                        String(bodyTypeOther || "") === value
                          ? "is-selected"
                          : ""
                      }`}
                      onClick={() =>
                        setBodyTypeOther(value)
                      }
                    >
                      {specialEquipmentSubtypeLabel(value)}
                    </button>
                  ))}
                </div>
              ) : null}
          </>
        ) : null}
        </div>
      </section>
    </div>
  );

  if (loading && !hydratedOnceRef.current) {
    return (
      <div className="profile-safe-bottom mx-auto w-full max-w-3xl p-4 text-slate-100">
        <div className="rounded-[30px] border border-white/10 bg-white/5 p-6 text-center text-sm text-slate-300">
          {tr("profile.loading", "Загрузка…")}
        </div>
      </div>
    );
  }

  if (isPending && !pendingEditMode) {
    return (
      <div className="profile-safe-bottom mx-auto w-full max-w-3xl p-4 text-slate-100">
        <section className="rounded-[34px] border border-emerald-300/25 bg-gradient-to-b from-emerald-300/15 to-white/[0.04] p-6 text-center shadow-2xl">
          <div className="text-5xl">✅</div>
          <div className="mt-4 text-2xl font-black">{tr("registrationPending.title", "Анкета отправлена")}</div>
          <div className="mx-auto mt-3 max-w-lg text-base leading-relaxed text-slate-200">
            {tr("registrationPending.text", "Диспетчер REISOR проверяет данные водителя и документы автомобиля. После одобрения доступ к заказам откроется автоматически.")}
          </div>
          <div className="mx-auto mt-5 max-w-md rounded-2xl border border-amber-300/25 bg-amber-300/10 p-4 text-amber-100">
            <div className="text-xs font-bold uppercase tracking-wider">{tr("registrationPending.statusLabel", "Статус")}</div>
            <div className="mt-1 text-xl font-black">{tr("registrationPending.status", "На проверке")}</div>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={() => loadProfile()} className="rounded-2xl bg-emerald-600 px-4 py-4 font-black text-white">
              {tr("registrationPending.check", "Проверить статус")}
            </button>
            <button type="button" onClick={() => setPendingEditMode(true)} className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 font-black text-white">
              {tr("registrationPending.edit", "Изменить анкету")}
            </button>
          </div>
        </section>
      </div>
    );
  }

  if (isPending && pendingEditMode) {
    return (
      <div className="profile-safe-bottom mx-auto w-full max-w-3xl p-4 text-slate-100">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-black">{tr("registrationPending.editTitle", "Изменение анкеты")}</div>
            <div className="mt-1 text-sm text-amber-200">{tr("registrationPending.editHint", "После изменений анкета останется на проверке.")}</div>
          </div>
        </div>
        {approvalFields}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <button type="button" disabled={saving} onClick={async () => { if (await saveProfile()) setPendingEditMode(false); }} className="rounded-2xl bg-emerald-600 px-4 py-4 font-black text-white disabled:opacity-50">
            {saving ? tr("profile.saving", "Сохраняем…") : tr("registrationPending.save", "Сохранить изменения")}
          </button>
          <button type="button" onClick={() => setPendingEditMode(false)} className="rounded-2xl border border-white/15 bg-white/10 px-4 py-4 font-black text-white">
            {tr("registrationPending.back", "Вернуться к статусу")}
          </button>
        </div>
      </div>
    );
  }

  if (st === "draft") {
    return (
      <div className="profile-safe-bottom mx-auto w-full max-w-3xl p-4 text-slate-100">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-2xl font-black">{tr("registrationReview.title", "Завершение регистрации")}</div>
            <div className="mt-1 text-sm text-slate-300">{tr("registrationReview.hint", "Проверьте данные и загрузите документы водителя и автомобиля.")}</div>
          </div>
        </div>

        {draftEditMode ? (
          <>
            {approvalFields}
            <button type="button" onClick={() => setDraftEditMode(false)} className="mt-4 w-full rounded-2xl border border-sky-300/25 bg-sky-300/10 px-4 py-3 font-black text-sky-100">
              {tr("registrationReview.editDone", "Готово — перейти к документам")}
            </button>
          </>
        ) : (
          <section className="rounded-[30px] border border-white/10 bg-white/5 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{tr("registrationReview.driverTitle", "Водитель")}</div>
                <div className="mt-1 text-lg font-black">{[form.lastName, form.firstName, form.middleName].filter(Boolean).join(" ") || "—"}</div>
                <div className="mt-2 text-sm text-sky-200">Telegram: {form.phoneMain || "—"}</div>
                {form.phoneExtra ? <div className="mt-1 text-sm text-slate-300">{tr("registrationReview.extraPhone", "Резервный номер")}: {form.phoneExtra}</div> : null}
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">{tr("profile.bodyType", "Тип кузова")}</div>
                <div className="mt-1 text-lg font-black">{["Другое", "Спецтехника"].includes(form.bodyType) ? (bodyTypeOther || "—") : (form.bodyType ? bodyTypeLabel(form.bodyType) : "—")}</div>
              </div>
            </div>
            <button type="button" onClick={() => setDraftEditMode(true)} className="mt-4 w-full rounded-2xl border border-white/15 bg-white/10 px-4 py-3 font-black text-white">
              {tr("registrationReview.edit", "Исправить данные")}
            </button>
          </section>
        )}

        <DriverDocsCamera
          profilePayload={approvalPayload}
          onSubmitted={() => {
            setStatus("pending");
            setPendingEditMode(false);
          }}
        />
      </div>
    );
  }

  const priorityScore = dp22jPriorityScore(driverTrust || { priorityScore: 0 });
  const priorityEarnedItems = Array.isArray(driverTrust?.earned || driverTrust?.breakdown)
    ? (driverTrust?.earned || driverTrust?.breakdown || []).filter((item) => item?.code === "completed_orders" || Number(item.points || 0) > 0)
    : [];
  // REISOR_STAGE22P2Q3_DRIVER_PRIORITY_ORDER_DISCIPLINE_POOL_AND_CLIENT_FEED_V1
  // Negative priority events are shown in History only.
  // Profile has no red aggregate penalty block like "-105".
  const priorityPenaltyItems = [];
  const priorityGoalItems = dp22nBuildGoalRows(tr, priorityEarnedItems);
  const priorityHistoryRows = Array.isArray(priorityHistory) && priorityHistory.length
    ? priorityHistory
    : dp22nPriorityRowsForHistory(priorityEarnedItems, priorityPenaltyItems); // REISOR_STAGE22N_E_PRIORITY_HISTORY_V1
  const priorityHistoryIsReal = Array.isArray(priorityHistory) && priorityHistory.length > 0;

  // REISOR_DRIVER_PROFILE_OVERVIEW_UI_V2
  // REISOR_DRIVER_PROFILE_PRIORITY_I18N_SETTINGS_V3: priority directly below identity; language lives in Settings; approved profile has no duplicate document block.
  // REISOR_DRIVER_MOBILE_PROFILE_FLAT_V2: mobile-only visual flattening; no business logic changes.
  // Presentation-only values. No API or business-state changes.
  const driverDisplayName =
    [form.firstName, form.lastName]
      .map((value) => trim(value))
      .filter(Boolean)
      .join(" ") ||
    tr(
      "profile.overviewDriverFallback",
      "Водитель REISOR"
    );

  const vehicleSummary =
    [
      trim(form.car),
      trim(form.carYear),
      trim(form.carNumber),
    ]
      .filter(Boolean)
      .join(" · ") ||
    tr(
      "profile.overviewVehicleEmpty",
      "Автомобиль не указан"
    );

  const bodySummary =
    trim(
      ["Другое", "Спецтехника"].includes(form.bodyType)
        ? bodyTypeOther
        : bodyTypeLabel(form.bodyType)
    ) ||
    tr(
      "profile.overviewBodyEmpty",
      "Кузов не указан"
    );

  const profileStatusLabel = isApproved
    ? tr(
        "profile.overviewStatusApproved",
        "Подтверждён"
      )
    : isPending
      ? tr(
          "profile.overviewStatusPending",
          "На проверке"
        )
      : isBlocked
        ? tr(
            "profile.overviewStatusBlocked",
            "Заблокирован"
          )
        : tr(
            "profile.overviewStatusDraft",
            "Заполняется"
          );

  const profileStatusTone = isApproved
    ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-200"
    : isPending
      ? "border-amber-400/30 bg-amber-400/10 text-amber-200"
      : isBlocked
        ? "border-red-400/30 bg-red-400/10 text-red-200"
        : "border-slate-600 bg-slate-800/60 text-slate-200";

  if (priorityScreen === "history") {
    return (
      <Dp22nPriorityHistoryScreen
        tr={tr}
        score={priorityScore}
        rows={priorityHistoryRows}
        realHistory={priorityHistoryIsReal}
        lang={activeLang}
        onBack={() => setPriorityScreen("profile")}
      />
    );
  }

  return (
    <div className="driver-profile-screen profile-safe-bottom mx-auto w-full max-w-3xl p-4 text-slate-100">

      <section
        className="driver-profile-overview overflow-hidden rounded-[30px] border
          border-white/10 bg-gradient-to-b
          from-white/[0.08] to-white/[0.03]
          shadow-2xl shadow-black/20"
      >
        <div className="driver-profile-overview-inner p-5">
          <div
            className="flex flex-col gap-4 sm:flex-row
              sm:items-start sm:justify-between"
          >
            <div className="driver-profile-identity flex min-w-0 items-center gap-4">
              <span
                className="driver-profile-avatar flex h-16 w-16 shrink-0 items-center
                  justify-center rounded-3xl border
                  border-white/10 bg-black/20 text-slate-100"
              >
                <UserRound size={31} strokeWidth={2.1} />
              </span>

              <div className="min-w-0">
                <div
                  className="text-xs font-black uppercase
                    tracking-[0.14em] text-slate-400"
                >
                  {tr(
                    "profile.overviewTitle",
                    "Профиль водителя"
                  )}
                </div>

                <div
                  className="driver-profile-name mt-1 truncate text-2xl font-black
                    leading-tight text-white"
                >
                  {driverDisplayName}
                </div>

                <div
                  className={`mt-2 inline-flex rounded-full
                    border px-3 py-1 text-xs font-black
                    ${profileStatusTone}`}
                >
                  {profileStatusLabel}
                </div>
              </div>
            </div>

          </div>

          {/* REISOR_STAGE22N_C_DRIVER_PRIORITY_PRODUCT_UI_V1 */}
          <section className="driver-priority-card mt-4 rounded-[30px] border border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.03] p-5 shadow-2xl shadow-black/20">
            <div className="text-center">
              <div className="text-sm font-black uppercase tracking-[0.18em] text-emerald-200/90">
                {tr("priority.title", "Приоритет REISOR")}
              </div>
              <Dp22nPriorityGauge score={priorityScore} tr={tr} />
              <div className="mt-5 text-2xl font-black leading-tight text-white">
                {dp22nPriorityStatusText(tr, priorityScore)}
              </div>
              <div className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-300">
                {tr("priority.subtitle", "Чем выше приоритет, тем заметнее ваши предложения в ленте клиентов.")}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setPriorityScreen("history")}
              className="driver-priority-history-link mt-5 flex w-full items-center justify-between rounded-3xl border border-white/10 bg-white/[0.06] px-5 py-4 text-left active:scale-[0.99]"
            >
              <span className="text-lg font-black text-white">{tr("priority.history.open", "История изменений приоритета")}</span>
              <span className="text-2xl text-slate-300">›</span>
            </button>
          </section>

          <div className="mt-4 flex items-start gap-3 px-1">
            <Truck className="mt-0.5 shrink-0 text-slate-400" size={18} />
            <div className="min-w-0">
              <div className="text-xs font-black uppercase tracking-[0.12em] text-slate-400">
                {tr("profile.overviewVehicle", "Транспорт")}
              </div>
              <div className="mt-1 text-sm font-black text-white">{vehicleSummary}</div>
              <div className="mt-0.5 text-xs text-slate-400">{bodySummary}</div>
            </div>
          </div>

          {isApproved ? (
            <div
              className="driver-profile-state-hint mt-4 rounded-2xl border
                border-emerald-400/20 bg-emerald-400/10
                px-4 py-3 text-xs leading-5
                text-emerald-100"
            >
              {tr(
                "profile.approved_lock_hint",
                "Редактирование отключено — правки через диспетчера"
              )}
            </div>
          ) : null}

          {isPending ? (
            <div
              className="driver-profile-state-hint mt-4 rounded-2xl border
                border-amber-400/20 bg-amber-400/10
                px-4 py-3 text-xs leading-5
                text-amber-100"
            >
              {tr(
                "profile.pending_hint",
                "Профиль на проверке. Вы можете вносить правки — при необходимости диспетчер попросит повторную проверку."
              )}
            </div>
          ) : null}

          {isBlocked ? (
            <div
              className="driver-profile-state-hint mt-4 rounded-2xl border
                border-red-400/20 bg-red-400/10
                px-4 py-3 text-xs leading-5 text-red-100"
            >
              {tr(
                "profile.blocked_hint",
                "Профиль заблокирован. Обратитесь к диспетчеру."
              )}
            </div>
          ) : null}

          {loading ? (
            <div className="mt-3 text-xs text-slate-400">
              {tr("profile.loading", "Загрузка…")}
            </div>
          ) : null}

          {error ? (
            <div
              className="mt-3 rounded-2xl border
                border-red-400/20 bg-red-400/10
                px-4 py-3 text-xs text-red-200"
            >
              {tr(
                "profile.load_failed",
                "Не удалось загрузить профиль"
              )}
              :{" "}
              {error === "NO_AUTH"
                ? tr(
                    "profile.no_auth",
                    "Нет авторизации. Вернитесь на экран входа."
                  )
                : error === "DRIVER_ID_MISSING"
                  ? tr(
                      "profile.no_driver_id",
                      "Не найден driver_id. Перезайдите в приложение."
                    )
                  : error === "HTTP_429"
                    ? tr(
                        "profile.too_many",
                        "Слишком много запросов. Подождите пару секунд…"
                      )
                    : error}
            </div>
          ) : null}
        </div>
      </section>

      <div className="driver-profile-sections mt-4 grid gap-4">
        {/* REISOR_STAGE97D3_DRIVER_WORK_STATUS_TOP_CARD_V1 */}
        <section id="driver-work-status" className="driver-profile-work-status rounded-[30px] border border-sky-300/20 bg-sky-300/10 p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-2xl font-black leading-tight">{tr("profile.workStatusTitle", "Статус работы")}</div>
              <div className="mt-1 text-sm leading-relaxed text-sky-100/85">
                {tr("profile.workStatusSubtitle", "Укажите, от чьего имени вы направляете предложения по заказам.")}
              </div>
            </div>
            {form.carrierStatus ? (
              <div className="w-fit rounded-full border border-sky-200/25 bg-black/20 px-3 py-1 text-xs font-bold text-sky-100">
                {tr("profile.workStatusDeclared", "Заявлено водителем")}
              </div>
            ) : null}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
            {/* REISOR_DRIVER_PROFILE_CARRIER_STATUS_NATIVE_SAFE_SELECT_V1 */}
              <NativeSafeSelect
                disabled={workStatusDisabled}
                className="w-full"
                buttonClassName={`${inputClass} text-left`}
                menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
                value={form.carrierStatus || ""}
                onChange={(next) => {
                  setForm((p) => ({
                    ...p,
                    carrierStatus: next,
                    inn: statusNeedsInn(next) ? p.inn : "",
                    carrierName: statusNeedsName(next) ? p.carrierName : "",
                  }));
                }}
                ariaLabel={tr("profile.workStatusHint", "Выберите, от чьего имени вы направляете предложения по заказам.")}
                options={[
                  { value: "", label: `${tr("profile.carrierStatus", "Статус работы")}…` },
                  ...[...CARRIER_STATUSES, ...(form.carrierStatus === "physical_park" ? LEGACY_CARRIER_STATUS_OPTIONS : [])].map((x) => ({
                    value: x.value,
                    label: tr(x.key, x.fallback),
                  })),
                ]}
              />

            {statusNeedsName(form.carrierStatus) ? (
              <input
                disabled={workStatusDisabled}
                className={inputClass}
                value={form.carrierName}
                onChange={(e) => setForm((p) => ({ ...p, carrierName: e.target.value.slice(0, 160) }))}
                placeholder={
                  form.carrierStatus === "company"
                    ? tr("profile.carrierCompanyName", "Название ООО / компании")
                    : tr("profile.carrierParkName", "Название парка / логиста")
                }
                title={tr("profile.carrierNameHint", "Название указывается со слов водителя. Подтверждение можно будет добавить позже.")}
              />
            ) : null}

            {statusNeedsInn(form.carrierStatus) ? (
              <input
                disabled={workStatusDisabled}
                inputMode="numeric"
                className={inputClass}
                value={form.inn}
                onChange={(e) => setForm((p) => ({ ...p, inn: e.target.value.replace(/\D/g, "").slice(0, 12) }))}
                placeholder={
                  form.carrierStatus === "company"
                    ? tr("profile.companyInn", "ИНН компании")
                    : tr("profile.inn", "ИНН")
                }
                title={tr("profile.innHint", "ИНН используется как заявленный водителем правовой статус работы.")}
              />
            ) : null}
          </div>

          {form.carrierStatus ? (
            <div className="mt-3 rounded-xl border border-sky-300/20 bg-black/15 px-3 py-2 text-xs text-sky-50">
              {tr(
                "profile.workStatusDisclaimer",
                "Статус указан водителем. REISOR не является перевозчиком и не оформляет перевозку от своего имени."
              )}
            </div>
          ) : null}

          {form.carrierStatus === "physical_park" ? (
            <div className="mt-3 rounded-xl border border-amber-300/20 bg-amber-300/10 px-3 py-2 text-xs text-amber-100">
              <div>
                {tr(
                  "profile.physicalParkHint",
                  "Физлицо может зарегистрироваться, но для доступа к заказам нужно выбрать парк экспедитора."
                )}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
                {/* REISOR_DRIVER_PROFILE_PARK_NATIVE_SAFE_SELECT_V1 */}
                  <NativeSafeSelect
                    disabled={workStatusDisabled || parkLoading || parkSaving}
                    className="w-full"
                    buttonClassName={`${inputClass} text-left`}
                    menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
                    value={form.selectedParkExpeditorId || ""}
                    onChange={(next) => setForm((p) => ({ ...p, selectedParkExpeditorId: next }))}
                    ariaLabel={tr("profile.parkPick", "Выберите парк экспедитора")}
                    options={[
                      {
                        value: "",
                        label: parkLoading ? tr("profile.parkLoading", "Загружаем парки…") : tr("profile.parkPick", "Выберите парк экспедитора"),
                      },
                      ...parkItems.map((p) => ({
                        value: p.id,
                        label: [p.name, p.city, p.country].filter(Boolean).join(" · "),
                      })),
                    ]}
                  />
                <button
                  type="button"
                  disabled={workStatusDisabled || parkSaving || !form.selectedParkExpeditorId}
                  className="rounded-xl bg-amber-500 px-3 py-2 text-sm font-bold text-slate-950 disabled:opacity-50"
                  onClick={() => selectPark(form.selectedParkExpeditorId)}
                >
                  {parkSaving ? tr("profile.parkSaving", "Сохраняем…") : tr("profile.parkSelect", "Выбрать парк")}
                </button>
              </div>
              <div className="mt-1 text-[11px] opacity-80">
                {tr("profile.parkInviteHint", "Если вашего парка нет в списке, попросите экспедитора подключить парк в REISOR.")}
              </div>
            </div>
          ) : null}

          <button
            type="button"
            disabled={workStatusDisabled || workStatusSaving}
            onClick={saveWorkStatus}
            className="mt-4 w-full rounded-2xl bg-sky-600 py-3 text-center font-bold text-white disabled:opacity-50"
          >
            {workStatusSaving ? tr("profile.workStatusSaving", "Сохраняем статус…") : tr("profile.workStatusSave", "Сохранить статус работы")}
          </button>
        </section>

        <details
          className="driver-profile-flat-details driver-priority-details group rounded-[30px] border
            border-white/10 bg-white/[0.04] p-5"
        >
          <summary
            className="flex cursor-pointer list-none
              items-center justify-between gap-3"
          >
            <span>
              <span className="block text-xl font-black">
                {tr(
                  "profile.priorityDetailsTitle",
                  "Как повысить приоритет"
                )}
              </span>

              <span
                className="mt-1 block text-xs leading-5
                  text-slate-400"
              >
                {tr(
                  "profile.priorityDetailsHint",
                  "Цели, начисленные баллы и правила роста"
                )}
              </span>
            </span>

            <ChevronDown
              className="shrink-0 text-slate-400
                transition-transform group-open:rotate-180"
              size={22}
            />
          </summary>

          <div className="mt-4 grid gap-4">
            <section
              className="driver-priority-subsection rounded-[26px] border
                border-white/10 bg-black/15 p-4"
            >
              <div className="text-2xl font-black leading-tight">{tr("priority.growthTitle", "Что улучшить")}</div>
          <div className="mt-1 text-sm leading-relaxed text-slate-400">
            {tr("priority.growthSubtitle", "Выполните пункты ниже, чтобы поднять предложения выше в ленте.")}
          </div>

          <div className="mt-3">
            {priorityGoalItems.length ? (
              priorityGoalItems.map((item) => (
                <Dp22nPriorityRow
                  key={item.code}
                  title={item.title}
                  hint={item.hint}
                  points={item.isCompletedPool ? item.current : item.points}
                  tone={item.isCompletedPool && Number(item.current || 0) < 0 ? "red" : "green"}
                  rightText={item.isCompletedPool ? `${dp22nSignedPoints(item.current)} / +${item.max}` : `+${item.points}`}
                />
              ))
            ) : (
              <div className="rounded-3xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm font-semibold text-emerald-100">
                {tr("priority.noGoals", "Все основные условия приоритета уже выполнены.")}
              </div>
            )}
          </div>
        </section>

        <section className="driver-priority-subsection rounded-[30px] border border-white/10 bg-white/[0.04] p-5">
          <div className="text-2xl font-black leading-tight">{tr("priority.earnedTitle", "Полученные баллы")}</div>
          <div className="mt-1 text-sm leading-relaxed text-slate-400">
            {tr("priority.earnedSubtitle", "Эти пункты уже учитываются в вашем текущем приоритете.")}
          </div>

          <div className="mt-3">
            {priorityEarnedItems.length ? (
              priorityEarnedItems.map((item) => (
                <Dp22nPriorityRow
                  key={item.code || item.label}
                  title={dp22nPriorityItemLabel(tr, item)}
                  hint={dp22nPriorityItemHint(tr, item)}
                  points={Number(item.points || 0)}
                  tone={Number(item.points || 0) < 0 ? "red" : "green"}
                />
              ))
            ) : (
              <div className="rounded-3xl border border-white/10 bg-black/20 p-4 text-sm text-slate-300">
                {tr("priority.noEarned", "Пока нет начисленных баллов.")}
              </div>
            )}
          </div>
        </section>

        {/* REISOR_STAGE22P2Q3_DRIVER_PRIORITY_ORDER_DISCIPLINE_POOL_AND_CLIENT_FEED_V1: red aggregate penalties block removed; see priority history. */}

        <div className="px-1 text-xs leading-relaxed text-slate-400">
          {tr("priority.note", "Баллы рассчитываются по данным профиля, завершённым заказам и подтверждённым событиям.")}
        </div>
          </div>
        </details>
      </div>

      {/* REISOR_DRIVER_WORK_COUNTRIES_PROFILE_MOUNT_V1 */}
      <DriverWorkCountriesPanel />

      <details
        open={!isApproved ? true : undefined}
        className="driver-profile-flat-details driver-data-details group rounded-[30px] border
          border-white/10 bg-white/[0.04] p-5"
      >
        <summary
          className="flex cursor-pointer list-none
            items-center justify-between gap-3"
        >
          <span>
            <span className="block text-xl font-black">
              {tr(
                "profile.dataDetailsTitle",
                "Данные и транспорт"
              )}
            </span>

            <span
              className="mt-1 block text-xs leading-5
                text-slate-400"
            >
              {tr(
                "profile.dataDetailsHint",
                "Личные данные, транспорт и реквизиты"
              )}
            </span>
          </span>

          <ChevronDown
            className="shrink-0 text-slate-400
              transition-transform group-open:rotate-180"
            size={22}
          />
        </summary>

        <div className="mt-4">
      {/* Основные поля */}
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <input disabled className={inputClass} value={form.phoneMain} placeholder={tr("registrationReview.telegramPhone", "Telegram-номер")} />
        <input
          disabled={disabled}
          className={inputClass}
          value={form.firstName}
          onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
          placeholder={tr("profile.firstName", "Имя")}
        />
        <input
          disabled={disabled}
          className={inputClass}
          value={form.lastName}
          onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
          placeholder={tr("profile.lastName", "Фамилия")}
        />

        <input
          disabled={disabled}
          className={inputClass}
          value={form.middleName}
          onChange={(e) => setForm((p) => ({ ...p, middleName: e.target.value }))}
          placeholder={tr("profile.patronymic", "Отчество")}
        />
        <input
          disabled={disabled}
          className={inputClass}
          value={form.phoneExtra}
          onChange={(e) => setForm((p) => ({ ...p, phoneExtra: e.target.value }))}
          placeholder={tr("registrationReview.extraPhoneOptional", "Резервный номер — необязательно")}
        />
        <input
          disabled={disabled}
          className={inputClass}
          value={form.car}
          onChange={(e) => setForm((p) => ({ ...p, car: e.target.value }))}
          placeholder={tr("profile.car", "Авто")}
        />

        <input
          disabled={disabled}
          className={inputClass}
          value={form.carYear}
          onChange={(e) => setForm((p) => ({ ...p, carYear: e.target.value }))}
          placeholder={tr("profile.carYear", "Год выпуска")}
        />
        <input
          disabled={disabled}
          className={inputClass}
          value={form.carNumber}
          onChange={(e) => setForm((p) => ({ ...p, carNumber: e.target.value }))}
          placeholder={tr("profile.plate", "Госномер")}
        />

        {/* CANON: тип кузова */}
        {/* REISOR_DRIVER_PROFILE_BODY_TYPE_NATIVE_SAFE_SELECT_V1 */}
          <NativeSafeSelect
            disabled={disabled}
            className="w-full"
            buttonClassName={`${inputClass} text-left`}
            menuClassName="absolute left-0 right-0 z-[99999] mt-2 overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl"
            value={form.bodyType || ""}
            onChange={(v) => {
              setForm((p) => ({ ...p, bodyType: v }));
              if (!["Другое", "Спецтехника"].includes(v)) setBodyTypeOther("");
            }}
            ariaLabel={tr("profile.bodyType", "Тип кузова")}
            options={[
              { value: "", label: `${tr("profile.bodyType", "Тип кузова")}…` },
              ...CANON_BODY_TYPES.map((x) => ({
                value: x,
                label: bodyTypeLabel(x),
              })),
            ]}
          />

        {/* CANON: уточнение кузова */}
        {["Другое", "Спецтехника"].includes(form.bodyType) ? (
          <>
            <input
            disabled={disabled}
            className={inputClass}
            value={bodyTypeOther}
            onChange={(e) => setBodyTypeOther(e.target.value)}
            placeholder={tr("profile.bodyTypeOther", "Уточните кузов")}
          />

            {form.bodyType === "Спецтехника" ? (
                <div
                  className="reisor-special-subtype-grid"
                  data-reisor-special-subtype-picker="profile"
                >
                  {SPECIAL_EQUIPMENT_SUBTYPES.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={`reisor-special-subtype-option ${
                        String(bodyTypeOther || "") === value
                          ? "is-selected"
                          : ""
                      }`}
                      onClick={() =>
                        setBodyTypeOther(value)
                      }
                    >
                      {specialEquipmentSubtypeLabel(value)}
                    </button>
                  ))}
                </div>
              ) : null}
          </>
        ) : null}

        <input
          disabled={disabled}
          className={inputClass}
          value={form.features}
          onChange={(e) => setForm((p) => ({ ...p, features: e.target.value }))}
          placeholder={tr("profile.features", "Особенности (через запятую)")}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <input
          disabled={disabled}
          className={inputClass}
          value={form.licenseNumber}
          onChange={(e) => setForm((p) => ({ ...p, licenseNumber: e.target.value }))}
          placeholder={tr("profile.license", "Лицензия/удостоверение")}
        />
        <input
          disabled={disabled}
          className={inputClass}
          value={form.cardNumber}
          onChange={(e) => setForm((p) => ({ ...p, cardNumber: e.target.value }))}
          placeholder={tr("profile.card", "Карта")}
        />
        <input
          disabled={disabled}
          className={inputClass}
          value={form.bank}
          onChange={(e) => setForm((p) => ({ ...p, bank: e.target.value }))}
          placeholder={tr("profile.bank", "Банк")}
        />
      </div>

      

      <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4">
        {/* REISOR_STAGE22N_B_DRIVER_PRIORITY_UI_COPY_V1 */}
        <div className="text-sm font-black">{tr("priority.paymentTitle", "Условия оплаты для Приоритета REISOR")}</div>
        <div className="mt-2 text-xs opacity-75">
          {tr("priority.paymentHint", "Галочка показывает готовность работать по безналу и с НДС. Баллы начисляются после завершённого заказа с такими условиями.")}
        </div>
        <div className="mt-3 grid gap-2">
          <label className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-3 py-3 text-sm font-bold">
            <input
              type="checkbox"
              disabled={disabled}
              checked={!!form.paymentCashless}
              onChange={(e) => setForm((p) => ({ ...p, paymentCashless: !!e.target.checked, paymentVat: e.target.checked ? p.paymentVat : false }))}
            />
            {tr("priority.cashlessCheckbox", "Работаю по безналу")} <span className="text-emerald-200">{tr("priority.afterCompletedOrderPlus20", "после заказа +20")}</span>
          </label>
          <label className={`flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 px-3 py-3 text-sm font-bold ${!form.paymentCashless ? "opacity-50" : ""}`}>
            <input
              type="checkbox"
              disabled={disabled || !form.paymentCashless}
              checked={!!form.paymentVat}
              onChange={(e) => setForm((p) => ({ ...p, paymentVat: !!e.target.checked }))}
            />
            {tr("priority.vatCheckbox", "Работаю с НДС")} <span className="text-emerald-200">{tr("priority.afterCompletedOrderPlus20", "после заказа +20")}</span>
          </label>
        </div>
      </div>

<button
        disabled={disabled || saving}
        onClick={saveProfile}
        className="mt-4 w-full rounded-2xl bg-emerald-600 py-3 text-center font-bold text-white disabled:opacity-50"
      >
        {saving ? tr("profile.saving", "Сохраняем…") : tr("profile.save", "Сохранить")}
      </button>
        </div>
      </details>
    </div>
  );
}
